import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Pencil, Building2, Server, Users, Receipt, Coins, UserPlus } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { Pagination } from '@/components/shared/Pagination';
import { useGetCompany } from '@/services/api/endpoints/CompanyService';
import { useGetMachines } from '@/services/api/endpoints/MachineService';
import { useGetUsers } from '@/services/api/endpoints/UserService';
import { useGetTransactions } from '@/services/api/endpoints/TransactionService';
import { useGetRevenue } from '@/services/api/endpoints/AnalyticsService';
import { formatDate, formatDateOnly, formatAmount } from '@/lib/utils';
import { CompanyModal } from './CompanyModal';
import { AssignUserModal } from './AssignUserModal';
import { CompanyPaymentConfigTab } from './CompanyPaymentConfigTab';
import { CompanyFiscalConfigTab } from './CompanyFiscalConfigTab';
import { CompanyLogoSection } from './CompanyLogoSection';
import { OrdersPage } from '@/pages/OrdersPage/OrdersPage';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

type Tab = 'info' | 'machines' | 'users' | 'transactions' | 'topups' | 'revenue' | 'payment' | 'fiscal';

export function CompanyDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [activeTab, setActiveTab] = useState<Tab>('info');
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [assignUserOpen, setAssignUserOpen] = useState(false);
  const [machinesPage, setMachinesPage] = useState(1);
  const [usersPage, setUsersPage] = useState(1);
  const [txPage, setTxPage] = useState(1);

  const { data: company, isLoading } = useGetCompany(id!);
  const { data: machinesData } = useGetMachines(
    machinesPage, 10, undefined, 'name', 'asc', undefined, undefined, id
  );
  const { data: usersData } = useGetUsers(
    usersPage, 10, undefined, 'createdAt', 'desc', undefined, undefined, id
  );
  const { data: txData } = useGetTransactions(
    txPage, 10, undefined, 'createdAt', 'desc'
  );
  const { data: revenueData } = useGetRevenue('daily', undefined, undefined, id);

  const tabs: { key: Tab; label: string }[] = [
    { key: 'info', label: t('companies.info') },
    { key: 'machines', label: t('companies.machines') },
    { key: 'users', label: t('companies.users') },
    { key: 'transactions', label: t('companies.companyTransactions') },
    { key: 'topups', label: t('topupOrders.tabTitle') || 'Top-ups' },
    { key: 'revenue', label: t('companies.companyRevenue') },
    { key: 'payment', label: t('payments.tabPayment') || 'Payment' },
    { key: 'fiscal', label: t('payments.tabFiscal') || 'Fiscal' },
  ];

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-64" />
      </div>
    );
  }

  if (!company) {
    return (
      <div className="p-6 text-center text-muted-foreground">
        {t('common.noResults')}
      </div>
    );
  }

  const chartData = revenueData?.map(item => ({
    ...item,
    dateFormatted: new Date(item.date).toLocaleDateString('sr-Latn', { day: 'numeric', month: 'short' }),
  })) || [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/companies')}>
          <ArrowLeft className="w-4 h-4 mr-1" />
          {t('common.back')}
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold">{company.name}</h1>
          <p className="text-sm text-muted-foreground">{company.email}</p>
        </div>
        <StatusBadge status={company.status} />
        <Button variant="outline" size="sm" onClick={() => setEditModalOpen(true)}>
          <Pencil className="w-4 h-4 mr-1" />
          {t('common.edit')}
        </Button>
      </div>

      {/* Tabs */}
      <div className="border-b">
        <nav className="flex gap-4">
          {tabs.map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`pb-3 text-sm font-medium transition-colors border-b-2 ${
                activeTab === tab.key
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'info' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">{t('companies.info')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <InfoRow label={t('companies.name')} value={company.name} />
              <InfoRow label={t('companies.email')} value={company.email || '-'} />
              <InfoRow label={t('companies.phone')} value={company.phone || '-'} />
              <InfoRow label={t('companies.address')} value={company.address || '-'} />
              <InfoRow label={t('common.status')} value={<StatusBadge status={company.status} />} />
              <InfoRow label={t('users.createdAt')} value={formatDateOnly(company.createdAt)} />
            </CardContent>
          </Card>
          <div className="space-y-4">
            <CompanyLogoSection company={company} />
            <Card>
              <CardContent className="pt-6 flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Server className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{company.machineCount || 0}</p>
                  <p className="text-sm text-muted-foreground">{t('companies.machines')}</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6 flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-success/10 flex items-center justify-center">
                  <Users className="w-6 h-6 text-success" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{company.userCount || 0}</p>
                  <p className="text-sm text-muted-foreground">{t('companies.users')}</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {activeTab === 'machines' && (
        <Card>
          <CardContent className="p-0">
            <>
              {/* Desktop Table */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="text-left p-4 text-sm font-medium text-muted-foreground">{t('machines.name')}</th>
                      <th className="text-left p-4 text-sm font-medium text-muted-foreground">{t('machines.code')}</th>
                      <th className="text-left p-4 text-sm font-medium text-muted-foreground">{t('machines.deviceId')}</th>
                      <th className="text-center p-4 text-sm font-medium text-muted-foreground">{t('machines.connectionStatus')}</th>
                      <th className="text-center p-4 text-sm font-medium text-muted-foreground">{t('common.status')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {machinesData?.items.map(m => (
                      <tr
                        key={m.id}
                        className="border-b hover:bg-muted/30 transition-colors cursor-pointer"
                        onClick={() => navigate(`/machines/${m.id}`)}
                      >
                        <td className="p-4 text-sm font-medium">{m.name}</td>
                        <td className="p-4 text-sm text-muted-foreground">{m.code}</td>
                        <td className="p-4 text-sm text-muted-foreground font-mono text-xs">{m.deviceId}</td>
                        <td className="p-4 text-center">
                          <Badge variant={m.connectionStatus === 'online' ? 'default' : 'secondary'}>
                            {m.connectionStatus}
                          </Badge>
                        </td>
                        <td className="p-4 text-center">
                          <StatusBadge status={m.status} />
                        </td>
                      </tr>
                    ))}
                    {machinesData?.items.length === 0 && (
                      <tr>
                        <td colSpan={5} className="p-8 text-center text-muted-foreground">{t('common.noResults')}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Mobile Cards */}
              <div className="md:hidden divide-y">
                {machinesData?.items.map(m => (
                  <div
                    key={m.id}
                    className="p-4 space-y-2 active:bg-muted/30 cursor-pointer"
                    onClick={() => navigate(`/machines/${m.id}`)}
                  >
                    <div className="font-medium text-sm">{m.name}</div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">{m.code}</span>
                      <span className="text-muted-foreground font-mono text-xs">{m.deviceId}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant={m.connectionStatus === 'online' ? 'default' : 'secondary'}>
                        {m.connectionStatus}
                      </Badge>
                      <StatusBadge status={m.status} />
                    </div>
                  </div>
                ))}
                {machinesData?.items.length === 0 && (
                  <div className="p-8 text-center text-muted-foreground">{t('common.noResults')}</div>
                )}
              </div>
            </>
          </CardContent>
          {machinesData?.pagination && machinesData.pagination.totalPages > 1 && (
            <div className="p-4">
              <Pagination page={machinesPage} totalPages={machinesData.pagination.totalPages} onPageChange={setMachinesPage} />
            </div>
          )}
        </Card>
      )}

      {activeTab === 'users' && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-base">{t('companies.users')}</CardTitle>
            <Button size="sm" className="gap-2" onClick={() => setAssignUserOpen(true)}>
              <UserPlus className="h-4 w-4" />
              {t('companies.assignUser')}
            </Button>
          </CardHeader>
          <CardContent className="p-0">
            <>
              {/* Desktop Table */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="text-left p-4 text-sm font-medium text-muted-foreground">{t('users.email')}</th>
                      <th className="text-left p-4 text-sm font-medium text-muted-foreground">{t('users.firstName')}</th>
                      <th className="text-left p-4 text-sm font-medium text-muted-foreground">{t('users.lastName')}</th>
                      <th className="text-center p-4 text-sm font-medium text-muted-foreground">{t('users.role')}</th>
                      <th className="text-center p-4 text-sm font-medium text-muted-foreground">{t('common.status')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {usersData?.items.map(u => (
                      <tr key={u.id} className="border-b hover:bg-muted/30 transition-colors">
                        <td className="p-4 text-sm font-medium">{u.email}</td>
                        <td className="p-4 text-sm">{u.firstName || '-'}</td>
                        <td className="p-4 text-sm">{u.lastName || '-'}</td>
                        <td className="p-4 text-center">
                          <Badge variant="outline">{t(`users.roles.${u.role}`)}</Badge>
                        </td>
                        <td className="p-4 text-center">
                          <StatusBadge status={u.status} />
                        </td>
                      </tr>
                    ))}
                    {usersData?.items.length === 0 && (
                      <tr>
                        <td colSpan={5} className="p-8 text-center text-muted-foreground">{t('common.noResults')}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Mobile Cards */}
              <div className="md:hidden divide-y">
                {usersData?.items.map(u => (
                  <div key={u.id} className="p-4 space-y-2">
                    <div className="font-medium text-sm">{u.email}</div>
                    <div className="text-sm text-muted-foreground">
                      {u.firstName || '-'} {u.lastName || '-'}
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline">{t(`users.roles.${u.role}`)}</Badge>
                      <StatusBadge status={u.status} />
                    </div>
                  </div>
                ))}
                {usersData?.items.length === 0 && (
                  <div className="p-8 text-center text-muted-foreground">{t('common.noResults')}</div>
                )}
              </div>
            </>
          </CardContent>
          {usersData?.pagination && usersData.pagination.totalPages > 1 && (
            <div className="p-4">
              <Pagination page={usersPage} totalPages={usersData.pagination.totalPages} onPageChange={setUsersPage} />
            </div>
          )}
        </Card>
      )}

      {activeTab === 'transactions' && (
        <Card>
          <CardContent className="p-0">
            <>
              {/* Desktop Table */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="text-left p-4 text-sm font-medium text-muted-foreground">{t('transactions.date')}</th>
                      <th className="text-left p-4 text-sm font-medium text-muted-foreground">{t('transactions.user')}</th>
                      <th className="text-left p-4 text-sm font-medium text-muted-foreground">{t('transactions.product')}</th>
                      <th className="text-right p-4 text-sm font-medium text-muted-foreground">{t('transactions.amount')}</th>
                      <th className="text-center p-4 text-sm font-medium text-muted-foreground">{t('common.status')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {txData?.items.map(tx => (
                      <tr key={tx.id} className="border-b hover:bg-muted/30 transition-colors">
                        <td className="p-4 text-sm">{formatDate(tx.createdAt)}</td>
                        <td className="p-4 text-sm">{tx.user?.email || '-'}</td>
                        <td className="p-4 text-sm">{tx.productName || '-'}</td>
                        <td className="p-4 text-sm text-right font-medium">{formatAmount(tx.amount)}</td>
                        <td className="p-4 text-center">
                          <StatusBadge status={tx.status} />
                        </td>
                      </tr>
                    ))}
                    {txData?.items.length === 0 && (
                      <tr>
                        <td colSpan={5} className="p-8 text-center text-muted-foreground">{t('common.noResults')}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Mobile Cards */}
              <div className="md:hidden divide-y">
                {txData?.items.map(tx => (
                  <div key={tx.id} className="p-4 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-muted-foreground">{formatDate(tx.createdAt)}</span>
                      <span className="text-sm">{tx.user?.email || '-'}</span>
                    </div>
                    <div className="text-sm text-muted-foreground">{tx.productName || '-'}</div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">{formatAmount(tx.amount)}</span>
                      <StatusBadge status={tx.status} />
                    </div>
                  </div>
                ))}
                {txData?.items.length === 0 && (
                  <div className="p-8 text-center text-muted-foreground">{t('common.noResults')}</div>
                )}
              </div>
            </>
          </CardContent>
          {txData?.pagination && txData.pagination.totalPages > 1 && (
            <div className="p-4">
              <Pagination page={txPage} totalPages={txData.pagination.totalPages} onPageChange={setTxPage} />
            </div>
          )}
        </Card>
      )}

      {activeTab === 'revenue' && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t('revenue.revenueOverTime')}</CardTitle>
          </CardHeader>
          <CardContent>
            {chartData.length === 0 ? (
              <div className="h-64 flex items-center justify-center border border-dashed rounded-lg bg-muted/20 text-muted-foreground text-sm">
                {t('common.noResults')}
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={288}>
                <AreaChart data={chartData} margin={{ top: 5, right: 5, left: -15, bottom: 5 }}>
                  <defs>
                    <linearGradient id="companyRevGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#2563eb" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#2563eb" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="dateFormatted" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
                  <YAxis
                    tick={{ fontSize: 11 }}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(0)}K` : v.toString()}
                  />
                  <Tooltip />
                  <Area
                    type="monotone"
                    dataKey="revenue"
                    stroke="#2563eb"
                    strokeWidth={2}
                    fill="url(#companyRevGrad)"
                    dot={false}
                    activeDot={{ r: 4 }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      )}

      {activeTab === 'topups' && <OrdersPage fixedCompanyId={id} embedded />}

      {activeTab === 'payment' && <CompanyPaymentConfigTab companyId={id!} />}

      {activeTab === 'fiscal' && <CompanyFiscalConfigTab companyId={id!} />}

      <CompanyModal
        open={editModalOpen}
        onOpenChange={setEditModalOpen}
        company={company}
      />

      <AssignUserModal
        open={assignUserOpen}
        onOpenChange={setAssignUserOpen}
        companyId={id!}
      />
    </div>
  );
}

function InfoRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex justify-between items-center py-1">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="text-sm font-medium">{value}</span>
    </div>
  );
}
